// IMPORTS

// External Libraries
import { defineStore } from 'pinia';

// Type - Read models
import type { TeacherDashboardReadModel } from '@c1/dashboard.types.read-models';

//Types - Entities
import type { InstitutionalSchoolInformation } from '@c1/types.entities';

interface SchoolsStoreState {
  /**
   * All schools that have been "Joined" by the user.
   *
   * Learners and Teachers can join multiple schools and can
   * have multiple class enrollment across those schools.
   *
   * A teacher could have joined a school, but not yet setup
   * or enrolled in any class.
   */

  schools?: TeacherDashboardReadModel['schools'];
  pendingSchoolRequests?: InstitutionalSchoolInformation[];
}

export const useTeacherSchoolsStore = defineStore('TeacherSchools', {
  // Internal state. Initialised as EMPTY
  state: (): SchoolsStoreState => ({}),

  getters: {
    isLoaded: (state) => !!state.schools,
    arePendingSchoolRequestsLoaded: (state) => !!state.pendingSchoolRequests
  },

  actions: {
    /**
     * Reset the school store to their initial state
     *
     * This is useful when the underlying data
     * is no longer valid or needs to be refreshed.
     */
    reset: function () {
      this.schools = undefined;
    },
    /**
     * Load the store from external callers.
     */
    async load(schoolsStoreState: SchoolsStoreState) {
      // update schools sent by external pages/components in the store
      this.schools = schoolsStoreState.schools;
    },

    /**
     * Update the store from external callers.
     */
    update(schoolsStoreState: SchoolsStoreState) {
      this.schools = Object.assign(
        this.schools ?? {},
        schoolsStoreState.schools
      );
    },

    /**
     * Load the pendingSchoolRequests from external callers.
     */
    async loadPendingSchoolRequests(
      pendingSchoolRequests: InstitutionalSchoolInformation[]
    ) {
      // update schools sent by external pages/components in the store
      this.pendingSchoolRequests = pendingSchoolRequests;
    }
  }
});
